// SharedScreenStreamManager.ts
export interface SharedStreamOptions {
  preferredWidth?: number;
  preferredHeight?: number;
  frameRate?: number;
  includeAudio?: boolean;
}

export interface StreamConsumer {
  id: string;
  type: "screenshot" | "recording";
  onStreamReady?: (stream: MediaStream) => void;
  onStreamEnded?: () => void;
}

class SharedScreenStreamManager {
  private static instance: SharedScreenStreamManager;
  private currentStream: MediaStream | null = null;
  private consumers: Map<string, StreamConsumer> = new Map();
  private isInitializing = false;
  private initPromise: Promise<MediaStream | null> | null = null;

  private constructor() {}

  static getInstance(): SharedScreenStreamManager {
    if (!SharedScreenStreamManager.instance) {
      SharedScreenStreamManager.instance = new SharedScreenStreamManager();
    }
    return SharedScreenStreamManager.instance;
  }

  // Register a consumer (screenshot or recording component)
  registerConsumer(consumer: StreamConsumer): void {
    console.log(`📝 Registering ${consumer.type} consumer:`, consumer.id);
    this.consumers.set(consumer.id, consumer);
  }

  // Unregister a consumer
  unregisterConsumer(consumerId: string): void {
    console.log(`🗑️ Unregistering consumer:`, consumerId);
    this.consumers.delete(consumerId);

    // If no consumers left, clean up the stream
    if (this.consumers.size === 0) {
      this.cleanup();
    }
  }

  // Get or create shared stream with single popup
  async getSharedStream(
    options: SharedStreamOptions = {}
  ): Promise<MediaStream | null> {
    // If we already have an active stream, return it
    if (this.currentStream && this.currentStream.active) {
      console.log("✅ Reusing existing stream - no popup needed");
      return this.currentStream;
    }

    // If we're already initializing, wait for that to complete
    if (this.isInitializing && this.initPromise) {
      console.log("⏳ Waiting for existing initialization...");
      return this.initPromise;
    }

    // Start new initialization
    this.isInitializing = true;
    this.initPromise = this.initializeStream(options);

    try {
      const stream = await this.initPromise;
      return stream;
    } finally {
      this.isInitializing = false;
      this.initPromise = null;
    }
  }

  private async initializeStream(
    options: SharedStreamOptions
  ): Promise<MediaStream | null> {
    try {
      console.log("🖥️ Requesting screen access (popup will appear once)...");

      // Clean up any existing stream
      this.cleanup();

      // Get responsive video constraints
      const screenWidth = window.screen?.width || 1920;
      const screenHeight = window.screen?.height || 1080;

      let idealWidth = options.preferredWidth || screenWidth;
      let idealHeight = options.preferredHeight || screenHeight;

      // Optimize for common desktop resolutions
      if (screenWidth >= 3840) {
        idealWidth = 3840;
        idealHeight = 2160;
      } else if (screenWidth >= 2560) {
        idealWidth = screenWidth;
        idealHeight = screenHeight;
      } else if (screenWidth >= 1920) {
        idealWidth = 1920;
        idealHeight = 1080;
      }

      // ✅ FIX 1: Always request audio for recordings
      const isRecordingRequest = this.hasRecordingConsumer();
      const shouldRequestAudio = isRecordingRequest || options.includeAudio !== false;

      console.log("🎤 Audio request decision:", {
        isRecordingRequest,
        shouldRequestAudio,
        explicitIncludeAudio: options.includeAudio
      });

      // Request display media with both video and system audio
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: idealWidth },
          height: { ideal: idealHeight },
          frameRate: { ideal: options.frameRate || 30 },
          mediaSource: "screen" as any,
        } as MediaTrackConstraints,
        audio: shouldRequestAudio, // ✅ Always true for recordings
        // @ts-ignore - Browser-specific optimizations
        preferCurrentTab: false,
        selfBrowserSurface: "exclude",
        systemAudio: "include",
      });

      console.log("✅ Display stream obtained:", {
        videoTracks: displayStream.getVideoTracks().length,
        audioTracks: displayStream.getAudioTracks().length
      });

      // ✅ FIX 2: Always try to get microphone for recording
      let microphoneStream: MediaStream | null = null;
      if (isRecordingRequest) {
        try {
          console.log("🎤 Requesting microphone access for recording...");
          microphoneStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 48000,
              channelCount: 2
            },
          });
          console.log("✅ Microphone access granted");
        } catch (micError) {
          console.warn("⚠️ Microphone access denied:", micError);
          // ✅ FIX 3: Show warning but continue with system audio only
          console.warn("⚠️ Recording will use system audio only (no microphone)");
        }
      }

      // ✅ FIX 4: Properly combine streams with AudioContext for better audio mixing
      let finalStream: MediaStream;
      
      if (microphoneStream && displayStream.getAudioTracks().length > 0) {
        // Case 1: We have both system audio and microphone - mix them
        console.log("🔊 Mixing system audio + microphone audio");
        finalStream = await this.mixAudioStreams(displayStream, microphoneStream);
      } else if (microphoneStream) {
        // Case 2: Only microphone audio available
        console.log("🔊 Using microphone audio only");
        finalStream = new MediaStream();
        displayStream.getVideoTracks().forEach((track) => {
          finalStream.addTrack(track);
        });
        microphoneStream.getAudioTracks().forEach((track) => {
          finalStream.addTrack(track);
        });
      } else if (displayStream.getAudioTracks().length > 0) {
        // Case 3: Only system audio available
        console.log("🔊 Using system audio only");
        finalStream = displayStream;
      } else {
        // Case 4: No audio at all (screenshots or audio denied)
        console.log("🔇 No audio tracks available");
        finalStream = displayStream;
        
        // ✅ FIX 5: Warn user if recording without audio
        if (isRecordingRequest) {
          console.warn("⚠️ WARNING: Recording started without audio!");
        }
      }

      this.currentStream = finalStream;

      // Set up stream end listeners
      finalStream.getTracks().forEach((track) => {
        track.addEventListener("ended", () => {
          console.log(`📺 Stream track ended (${track.kind}) - user stopped sharing`);
          this.handleStreamEnd();
        });
      });

      // Notify all consumers that stream is ready
      this.consumers.forEach((consumer) => {
        if (consumer.onStreamReady) {
          consumer.onStreamReady(finalStream);
        }
      });

      console.log("✅ Shared stream initialized successfully");
      console.log(`📹 Video tracks: ${finalStream.getVideoTracks().length}`);
      console.log(`🔊 Audio tracks: ${finalStream.getAudioTracks().length}`);
      
      // ✅ Log detailed audio track info
      finalStream.getAudioTracks().forEach((track, index) => {
        console.log(`🔊 Audio track ${index + 1}:`, {
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState
        });
      });

      return finalStream;
    } catch (error) {
      console.error("❌ Failed to initialize shared stream:", error);
      this.currentStream = null;
      return null;
    }
  }

  // ✅ NEW: Mix audio streams using Web Audio API for better quality
  private async mixAudioStreams(
    displayStream: MediaStream,
    microphoneStream: MediaStream
  ): Promise<MediaStream> {
    try {
      const audioContext = new AudioContext();
      const destination = audioContext.createMediaStreamDestination();

      // Add system audio
      const systemAudioTracks = displayStream.getAudioTracks();
      if (systemAudioTracks.length > 0) {
        const systemSource = audioContext.createMediaStreamSource(
          new MediaStream(systemAudioTracks)
        );
        systemSource.connect(destination);
        console.log("✅ Connected system audio to mixer");
      }

      // Add microphone audio
      const micAudioTracks = microphoneStream.getAudioTracks();
      if (micAudioTracks.length > 0) {
        const micSource = audioContext.createMediaStreamSource(
          new MediaStream(micAudioTracks)
        );
        micSource.connect(destination);
        console.log("✅ Connected microphone audio to mixer");
      }

      // Create final stream with video + mixed audio
      const finalStream = new MediaStream();
      
      // Add video tracks
      displayStream.getVideoTracks().forEach((track) => {
        finalStream.addTrack(track);
      });

      // Add mixed audio track
      destination.stream.getAudioTracks().forEach((track) => {
        finalStream.addTrack(track);
      });

      console.log("✅ Audio streams mixed successfully");
      return finalStream;
    } catch (error) {
      console.error("❌ Audio mixing failed, falling back to simple combination:", error);
      
      // Fallback: simple combination without mixing
      const fallbackStream = new MediaStream();
      displayStream.getTracks().forEach((track) => fallbackStream.addTrack(track));
      microphoneStream.getAudioTracks().forEach((track) => fallbackStream.addTrack(track));
      
      return fallbackStream;
    }
  }

  private hasRecordingConsumer(): boolean {
    return Array.from(this.consumers.values()).some(
      (consumer) => consumer.type === "recording"
    );
  }

  private handleStreamEnd(): void {
    console.log("🛑 Stream ended - notifying consumers");

    // Notify all consumers that stream ended
    this.consumers.forEach((consumer) => {
      if (consumer.onStreamEnded) {
        consumer.onStreamEnded();
      }
    });

    // Clean up
    this.currentStream = null;
  }

  // Get current stream without triggering new popup
  getCurrentStream(): MediaStream | null {
    return this.currentStream && this.currentStream.active
      ? this.currentStream
      : null;
  }

  // Check if stream is active
  isStreamActive(): boolean {
    return !!(this.currentStream && this.currentStream.active);
  }

  // Clean up resources
  cleanup(): void {
    console.log("🧹 Cleaning up shared stream...");

    if (this.currentStream) {
      this.currentStream.getTracks().forEach((track) => {
        console.log(`🛑 Stopping ${track.kind} track:`, track.label);
        track.stop();
      });
      this.currentStream = null;
    }
  }

  // Force cleanup (useful for component unmount)
  forceCleanup(): void {
    this.consumers.clear();
    this.cleanup();
  }
}

export default SharedScreenStreamManager;